/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { api, ApiError } from "core/api";
import { Button, Card, Input, Form, FieldGroup, Select, Switch } from "core/components";
import { toTitleCase } from "core/helpers";
import { navigateTo } from "core/navigation";
import { ItemCategoryFormData, ItemCategorySchema } from "core/schema";
import { admin_item_categories_path, admin_item_category_path } from "routes";
import { IMaskInput } from "react-imask";

type ItemCategoryFormProps = {
  item_category: ItemCategoryFormData;
  item_classifications: Array<TaskClassificationFormData>;
};

const CostTypeMask = "COS00000";
const InventoryTypeMask = "00000000";

const ItemCategoryForm = ({ item_category, item_classifications }: ItemCategoryFormProps): JSX.Element => {
  const {
    control,
    register,
    handleSubmit,
    formState: { isSubmitting, errors }
  } = useForm<ItemCategoryFormData>({
    mode: "onChange",
    defaultValues: ItemCategorySchema.cast(item_category),
    resolver: yupResolver(ItemCategorySchema)
  });
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);
  const codeProps =
    item_category.item_type == "cost"
      ? {
          mask: CostTypeMask,
          maxLength: 8,
          placeholder: "COS10000"
        }
      : {
          mask: InventoryTypeMask,
          maxLength: 7,
          placeholder: "1010000"
        };

  const save = async (data: ItemCategoryFormData): Promise<unknown> => {
    try {
      if (data.id) {
        await api.put(admin_item_category_path(item_category), data);
      } else {
        await api.post(admin_item_categories_path(item_category), data);
      }

      navigateTo(admin_item_categories_path({ item_type: item_category.item_type }));
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  return (
    <React.StrictMode>
      <Form errors={formErrors}>
        <Card>
          <Card.Header
            title={item_category.id ? `Edit ${toTitleCase(item_category.item_type)} Type` : `New ${toTitleCase(item_category.item_type)} Type`}
          >
            <Button
              icon="arrow-left"
              iconProps={{ className: "mr-3" }}
              className="button--default mr-3"
              disabled={isSubmitting}
              title="Back"
              onClick={() => navigateTo(admin_item_categories_path({ item_type: item_category.item_type }))}
            />
            <Button
              icon="save"
              iconProps={{ className: "mr-3" }}
              className="button--brand mr-3"
              disabled={isSubmitting}
              title="Save"
              onClick={handleSubmit(async (data) => save(data))}
            />
          </Card.Header>
          <Card.Body>
            <Container>
              <Row>
                <Col lg={3} />
                <Col>
                  <Controller
                    name="active"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        label="Active"
                        info="Active or Inactive"
                        checked={field.value}
                        value={field.value?.toString()}
                        errorMessage={errors.active?.message}
                      />
                    )}
                  />
                  {item_category.item_type == "inventory" && (
                    <Controller
                      name="item_classification_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          label="Item Classification"
                          placeholder="Select Item Classification"
                          required
                          options={item_classifications}
                          getOptionLabel={(option) => option.name}
                          getOptionValue={(option) => option.id}
                          isOptionDisabled={(option) => !option.active}
                          onChange={(newOption) => {
                            field.onChange(newOption?.id);
                          }}
                          value={item_classifications.find((item_classification) => item_classification.id == field.value) || null}
                          errorMessage={errors.item_classification_id?.message}
                        />
                      )}
                    />
                  )}
                  <Controller
                    name="code"
                    control={control}
                    render={({ field }) => (
                      <FieldGroup label="Code" required errorMessage={errors.code?.message}>
                        <IMaskInput
                          value={field.value}
                          unmask={false}
                          inputRef={field.ref}
                          onAccept={(value) => {
                            field.onChange(value);
                          }}
                          className="field text-uppercase"
                          {...codeProps}
                        />
                      </FieldGroup>
                    )}
                  />
                  <Input {...register("name")} label="Name" required errorMessage={errors.name?.message} placeholder="Labor" maxLength={100} />
                </Col>
                <Col lg={3} />
              </Row>
            </Container>
          </Card.Body>
        </Card>
      </Form>
    </React.StrictMode>
  );
};

export default ItemCategoryForm;
